import { useState, useRef, FC, KeyboardEvent } from 'react';
import * as Sentry from '@sentry/react';
import { Autocomplete } from '@react-google-maps/api';
import { CoordinatesType, ProjectType } from '../types/dataTypes';
import styled from 'styled-components';
import tw from 'twin.macro';
import Icon from './Icon';
import { ButtonColor, ButtonSize, IconColor, IconSize, Icons } from '../enums';
import strings from '../language/Swedish';
import Button from './Button';
import useUpdateProjectName from '../api/mutations/useUpdateProjectName';
import useProjects from '../api/queries/useProjects';
import { useProjectContext } from '@/context/ProjectContext';
import { geocodeAddress } from '@/components/map/MapHelpers';

const StyledInput = styled.input<{ $isError: boolean }>`
  ${tw`flex-1 py-2 px-8 text-sm border rounded-full`};
  ${({ $isError }) => $isError ? tw`border-red-300` : tw`border-slate-300`};
  &:focus {
    ${tw`ring-emerald-500 border-emerald-500`};
      ${({ $isError }) => $isError ? tw`outline-red-300` : tw`outline-primary`};

  }
`;

const StyledIcon = styled(Icon)`
  ${tw`absolute left-2 rotate-180 scale-x-[-1]`};
`;

const Error = styled.div`
text-align:center;
`;

interface Props {
  onPlaceSelected: (coordinates: CoordinatesType) => void;
  project?: ProjectType;
}

const AutocompleteInput: FC<Props> = ({ onPlaceSelected, project }) => {
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const { refetch: refetchProjects } = useProjects();

  const { setProject } = useProjectContext();

  const { mutate: updateProjectNameMutation } = useUpdateProjectName({
    onSuccess: (data) => {
      refetchProjects();

      setProject((prevState) => ({
        ...prevState,
        name: data.name,
      }));
    },
  });

  const handlePlaceChanged = async (manualAddress: string | null = null) => {
    setError(false);
    if (autocomplete !== null && manualAddress === null) {
      const place = autocomplete.getPlace();
      const location = place.geometry?.location;
      const address = place.formatted_address;
      if (location) {
        onPlaceSelected({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
      if (
        project?.id &&
        project?.name === strings.data.noProjectName &&
        address
      ) {
        updateProjectNameMutation({
          id: project.id,
          newName: address,
        });
      }
    } else if (manualAddress) {
      try {
        const location = await geocodeAddress(manualAddress);
        onPlaceSelected({
          lat: location.lat(),
          lng: location.lng(),
        });

        if (project?.id && project?.name === strings.data.noProjectName) {
          updateProjectNameMutation({
            id: project.id,
            newName: manualAddress,
          });
        }
      } catch (error) {
        setError(true);
        Sentry.captureMessage(error);
      }
    }
  };

  const handleSearchClicked = () => {
    if (inputRef.current) {
      handlePlaceChanged(inputRef.current.value);
    }
  };

  const handleSearchInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputRef.current) {
      handlePlaceChanged(inputRef.current.value);
    }
  };

  return (
    <div>
      <div className="flex gap-2 items-center">
        <Autocomplete
          onLoad={(ac) => {
            ac.setComponentRestrictions({ country: 'SE' });
            setAutocomplete(ac);
          }}
          onPlaceChanged={handlePlaceChanged}>
          <StyledInput
            $isError={error}
            type="text"
            ref={inputRef}
            placeholder={strings.data.addAdress}
            style={{ width: '300px', height: '40px' }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={handleSearchInputKeyDown}
          />
        </Autocomplete>
        <StyledIcon
          icon={Icons.search}
          color={IconColor.gray}
          size={IconSize.medium}
        />
        <Button
          color={ButtonColor.primary}
          size={ButtonSize.large}
          onClick={handleSearchClicked}>
          {strings.data.search}
        </Button>
      </div>
      {error && <Error>{strings.data.noHitsFounds}</Error>}

    </div>
  );
};

export default AutocompleteInput;
